import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Input,
  Modal,
  ConfigProvider,
  theme,
  Form,
  Select,
  DatePicker,
  InputNumber,
  message,
  Switch,
} from 'antd';
import enUS from 'antd/locale/en_US';
import {
  X03,
  AddCircleContained,
  TwitterFillBrand,
  XCircleContained,
} from 'lib/lucky-x-icons-2';
import classNames from 'classnames';
import { getSocialTwitterInfoGlobally } from 'api/social/v1_social_twitter_info';
import dayjs from 'dayjs';
import { projectCreate } from 'api/project/v1_project_create';
import { useDebounceEffect, useThrottleFn, useUpdateEffect } from 'ahooks';
import { clone, isEmpty } from 'lodash';
import { projectTwitterOauthUrl } from 'api/project/project_twitter_oauth_url';
import { correct } from 'api/project/project_correct';
import { successMsg } from '@/components/PageElement/SuccessMessage';
import { errMsg } from '@/components/PageElement/ErrorMessage';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { sendProjectChangeMsg } from 'store/features/projectListSlice';
import { Eth, Bnb, Matic } from 'lib/lucky-x-icons-2';
import { BroadcastChannel } from 'broadcast-channel';
import { blockChainIcon, blockChainList } from 'lib/blockChain';
import solIcon from '@/assets/icons/blockchain/SOL.png';
import seiIcon from '@/assets/icons/blockchain/Sei.png';
import ingIcon from '@/assets/icons/blockchain/Injective.png';
import blastIcon from '@/assets/icons/blockchain/Blast.png';
import avalancheIcon from '@/assets/icons/blockchain/Avalanche.png';
import starknetIcon from '@/assets/icons/blockchain/Starknet.png';
import tonIcon from '@/assets/icons/blockchain/Ton.png';

interface IAddPrpjectProps {
  data?: any;
  open: boolean;
  onClose: () => void;
}

const AddPrpject: React.FunctionComponent<IAddPrpjectProps> = ({
  data,
  open,
  onClose,
}) => {
  const [form] = Form.useForm();
  const mintDate = Form.useWatch('mint_date', form);
  const mintTime = Form.useWatch('mint_time', form);
  const chain = Form.useWatch('chain', form);
  const [twValidateStatus, setTwValidateStatus] = useState<any>();
  const [twHelp, setTwHelp] = useState('');
  const [tw, setTw] = useState({
    name: '',
    twitter_username: '',
    logo: '',
  });
  const { curOperateProject } = useAppSelector((state) => state.projectDetail);
  const dispatch = useAppDispatch();
  const [guess, setGuess] = useState('');
  const [openFreeMint, setOpenFreeMint] = useState(false);

  const handlerRequest = useCallback(
    async (setName: boolean) => {
      const twitter_screen_name: string = form.getFieldValue(
        'twitter_screen_name'
      );
      const res = await getSocialTwitterInfoGlobally(
        twitter_screen_name?.replace(
          /^(https:\/\/twitter\.com\/|https:\/\/x\.com\/|https:\/\/X\.com\/)/i,
          ''
        )
      );

      if (res?.twitter_user_id) {
        setTw(res);
        if (setName) {
          form.setFieldValue('name', res.name);
          form.validateFields(['name']);
        }
      } else {
        setTwValidateStatus('error');
        setTwHelp('Twitter not found');
        return;
      }
      setTwValidateStatus(undefined);
      setTwHelp('');
    },
    [form]
  );
  const verfiyScreenName = useCallback(() => {
    const twitter_screen_name: string = form.getFieldValue(
      'twitter_screen_name'
    );
    if (!twitter_screen_name) {
      setTwValidateStatus('error');
      setTwHelp('Required');
      return true;
    }
    if (
      !/\b(https?:\/\/(?:www\.)?(?:twitter\.com|[xX]\.com))\/([a-zA-Z0-9_]{1,15})\/?\b/.test(
        twitter_screen_name
      )
    ) {
      setTwValidateStatus('error');
      setTwHelp('Invalid Twitter link');
      return true;
    }
    setTwValidateStatus(undefined);
    setTwHelp('');
    return false;
  }, [form]);

  const handlerSearch = useCallback(async () => {
    if (verfiyScreenName()) {
      return;
    }
    handlerRequest(true);
  }, [handlerRequest, verfiyScreenName]);

  const resetTw = useCallback(() => {
    setTw({
      name: '',
      twitter_username: '',
      logo: '',
    });
  }, []);

  const generateParams = useCallback(() => {
    const formData = form.getFieldsValue();
    formData.twitter_screen_name = formData.twitter_screen_name?.replace(
      /^(https:\/\/twitter\.com\/|https:\/\/x\.com\/|https:\/\/X\.com\/)/i,
      ''
    );
    if (formData.mint_date) {
      if (formData.mint_time) {
        const mintTime = dayjs(formData.mint_time);
        formData.mint_time = mintTime.utc().format('HH:mm:ss');
        formData.mint_date = dayjs(formData.mint_date)
          .set('h', mintTime.get('h'))
          .set('m', mintTime.get('m'))
          .set('s', mintTime.get('s'))
          .set('ms', 0)
          .utc()
          .format('x');
      } else {
        formData.mint_date = dayjs
          .utc(formData.mint_date)
          .set('h', 0)
          .set('m', 0)
          .set('s', 0)
          .set('ms', 0)
          .format('x');
      }
    }
    if (
      formData.dc_invite_link &&
      formData.dc_invite_link?.indexOf('http') === -1
    ) {
      formData.dc_invite_link = 'https://' + formData.dc_invite_link;
    }
    if (formData.website && formData.website?.indexOf('http') === -1) {
      formData.website = 'https://' + formData.website;
    }
    return { ...formData, id: data?.id };
  }, [form, data?.id]);

  const handlerClose = useCallback(() => {
    form.resetFields();
    resetTw();
    onClose();
    setOpenFreeMint(false);
  }, [onClose, form, resetTw]);

  const handlerAdd = useCallback(async () => {
    if (verfiyScreenName()) {
      return;
    }
    await form.validateFields();
    const data = generateParams();
    const res = await projectCreate(data);
    if (res === 0) {
      dispatch(sendProjectChangeMsg(Date.now()));
      message.success('Project added');
      setTimeout(() => {
        location.reload();
      }, 1000);
    }
    handlerClose();
  }, [form, verfiyScreenName, handlerClose, dispatch, generateParams]);

  const handlerUpdataBefore = useCallback(async () => {
    await form.validateFields();
    const res = await projectTwitterOauthUrl(
      data.id,
      location.origin + '/twitterOauthUrl'
    );
    window.open(res as any);
  }, [data?.id, form]);

  const handlerSubmit = useCallback(async () => {
    await form.validateFields();
    const data = generateParams();
    const res = await correct(data, false, '');
    if (res === 0) {
      successMsg('Report successful');
    } else {
      errMsg('Report failed');
    }
    handlerClose();
  }, [form, handlerClose, generateParams]);

  const handlerUpdataThrottle = useThrottleFn(
    async (token: string) => {
      const data = generateParams();
      if (!isEmpty(data)) {
        correct(data, true, token).then((res) => {
          if (res === 0) {
            successMsg('Saved successfully');
            handlerClose();
            setTimeout(() => {
              location.reload();
            }, 1000);
          }
        });
      }
    },
    { wait: 5000, trailing: false }
  );

  const chainIcon = useCallback(() => {
    if (
      chain === 'Ethereum' ||
      chain === 'Arbitrum' ||
      chain === 'Optimism' ||
      chain === 'ZKSync'
    ) {
      return <Eth size={16} />;
    } else if (chain === 'Polygon') {
      return <Matic size={16} />;
    } else if (chain === 'BTC_Ordinal') {
      return (
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/thumb/4/46/Bitcoin.svg/1920px-Bitcoin.svg.png"
          className="w-5"
          alt=""
        />
      );
    } else if (chain === 'Solana') {
      return <img src={solIcon.src} className="h-5 w-5 rounded-full" alt="" />;
    } else if (chain === 'Sei_Network') {
      return <img src={seiIcon.src} className="h-5 w-5 rounded-full" alt="" />;
    } else if (chain === 'Injective') {
      return <img src={ingIcon.src} className="h-5 w-5 rounded-full" alt="" />;
    } else if (chain === 'Blast') {
      return (
        <img src={blastIcon.src} className="h-5 w-5 rounded-full" alt="" />
      );
    } else if (chain === 'Avalanche') {
      return (
        <img src={avalancheIcon.src} className="h-5 w-5 rounded-full" alt="" />
      );
    } else if (chain === 'Starknet') {
      return (
        <img src={starknetIcon.src} className="h-5 w-5 rounded-full" alt="" />
      );
    } else if (chain === 'Ton') {
      return <img src={tonIcon.src} className="h-5 w-5 rounded-full" alt="" />;
    }
    return <Bnb size={16} />;
  }, [chain]);

  useEffect(() => {
    const temp = clone(data);
    if (!isEmpty(temp)) {
      if (temp.mint_date) {
        temp.mint_date = dayjs.utc(temp.mint_date).local();
      }
      if (temp.mint_time && temp.mint_date) {
        temp.mint_time = dayjs
          .utc(temp.mint_time, 'hh:mm:ss')
          .set('year', temp.mint_date.get('year'))
          .set('month', temp.mint_date.get('month'))
          .set('date', temp.mint_date.get('date'))
          .local();
      }
      temp.twitter_screen_name =
        'https://twitter.com/' + temp.twitter_screen_name;
      form.setFieldsValue(temp);

      if ((curOperateProject !== data?.id && !!data) || !open) {
        return;
      }
      handlerRequest(false);
    }
  }, [curOperateProject, data, form, handlerRequest, open]);
  const channel = useMemo(
    () =>
      typeof window === 'undefined'
        ? null
        : new BroadcastChannel('projectTwitterOauthUrl'),
    []
  );
  useEffect(() => {
    if (channel) {
      channel.onmessage = (e: any) => {
        if (open) {
          if (!e.token) {
            errMsg('Save failed');
          } else {
            handlerUpdataThrottle.run(e.token);
          }
        }
      };
    }
  }, [open]);

  useUpdateEffect(() => {
    if (mintTime && !mintDate) {
      form.setFieldValue('mint_date', dayjs());
    }
  }, [mintTime]);
  useEffect(() => {
    setGuess(dayjs.tz.guess());
  }, []);

  return (
    <ConfigProvider
      locale={enUS}
      theme={{
        token: { colorPrimary: '#6CB53F', fontFamily: 'var(--font-rubik)' },
        algorithm: theme.darkAlgorithm,
      }}
    >
      <Modal
        width={774}
        open={open}
        closeIcon={
          <X03
            size={24}
            onClick={handlerClose}
            className="cursor-pointer text-neutral-400 hover:text-neutral-300"
          />
        }
        title={
          data ? (
            <p className="text-2xl font-bold italic">
              Report incorrect data
              <p className="my-6 text-base text-neutral-400 [font-style:normal]">
                Enter any incorrect or missing info for {data?.name}
              </p>
            </p>
          ) : (
            <p className="text-2xl font-bold italic">Add Project</p>
          )
        }
        footer={null}
        rootClassName="bg-card-dark"
        onCancel={handlerClose}
      >
        <Form form={form} layout="vertical">
          <div className="rounded-2xl bg-body-dark p-6">
            <style jsx>{`
              div:global(.ant-input) {
                background-color: #14171a !important;
              }
              div:global(.ant-select-selector) {
                background-color: #14171a !important;
              }
            `}</style>
            <p className="mb-7 text-xl font-semibold">Project Info</p>
            <Form.Item
              className={classNames('w-full', {
                hidden: !!tw?.name,
              })}
              label={
                <div className="flex">
                  <TwitterFillBrand size={24} color="#00A3FF" /> Project Twitter
                  Link
                </div>
              }
              required
              validateStatus={twValidateStatus}
              help={twHelp}
            >
              <div className="flex items-center gap-5">
                <Form.Item noStyle name="twitter_screen_name">
                  <Input
                    size="large"
                    placeholder="https://twitter.com/LuckyGo_io"
                  />
                </Form.Item>

                <div onClick={handlerSearch} className="cursor-pointer">
                  <AddCircleContained size={32} className="" />
                </div>
              </div>
            </Form.Item>
            <div
              className={classNames(
                'relative mb-6 flex items-center gap-4 rounded-xl bg-white p-4',
                { hidden: !tw?.name }
              )}
            >
              <img className="h-16 w-16 rounded-full" src={tw?.logo} alt="" />
              <div className="flex flex-col justify-center">
                <p className="text-base font-semibold text-black">{tw?.name}</p>
                <p className="flex items-center text-sm text-black">
                  <TwitterFillBrand size={16} color="#00A3FF" />{' '}
                  <span>@{tw?.twitter_username}</span>
                </p>
              </div>
              {data === undefined && (
                <div onClick={resetTw} className="absolute right-4 top-4">
                  <XCircleContained
                    size={22}
                    className="cursor-pointer text-neutral-400 hover:text-neutral-300"
                  />
                </div>
              )}
            </div>
            <div className="flex flex-col gap-3 lg:flex-row">
              <Form.Item
                className="w-full"
                label="Project Name"
                name="name"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input size="large" placeholder="LuckyGo" disabled={true} />
              </Form.Item>
              <Form.Item
                className="w-full"
                label="Blockchain"
                name="chain"
                initialValue="Ethereum"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Select
                  size="large"
                  options={blockChainList.map((item) => ({
                    value: item?.chain,
                    label: (
                      <div className="mt-[6px] flex items-center gap-3">
                        <img
                          src={blockChainIcon(item?.chain)}
                          className="h-6 w-6 rounded-full"
                          alt=""
                        />
                        <div className="text-sm font-normal text-neutral-100">
                          {item?.chainName}
                        </div>
                      </div>
                    ),
                  }))}
                ></Select>
              </Form.Item>
            </div>
            <div className="flex flex-col gap-3 lg:flex-row">
              <Form.Item
                className="mb-0 w-full"
                label="Website"
                name="website"
                rules={[
                  {
                    pattern:
                      /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
                    message: 'Invalid Website link',
                  },
                ]}
              >
                <Input size="large" placeholder="https://luckygo.io/" />
              </Form.Item>
              <Form.Item
                className="mb-0 w-full"
                label="Discord Invite Link"
                name="dc_invite_link"
                rules={[
                  {
                    pattern:
                      /^(?:https?:\/\/)?discord(?:\.gg|(?:app)?\.com\/invite)\/([a-zA-Z0-9-]{2,32})$/,
                    message: 'Invalid Discord invite link',
                  },
                ]}
              >
                <Input size="large" placeholder="https://discord.gg/code" />
              </Form.Item>
            </div>
          </div>
          <div className="mt-6 rounded-2xl bg-body-dark p-6">
            <style jsx>{`
              div:global(.ant-input) {
                background-color: #14171a !important;
              }
              div:global(.ant-select-selector) {
                background-color: #14171a !important;
              }
              div:global(.ant-picker) {
                background-color: #14171a !important;
              }
              div:global(.ant-input-number) {
                background-color: #14171a !important;
              }
              div:global(.ant-input-number-affix-wrapper) {
                background-color: #14171a !important;
              }
              div:global(.ant-switch .ant-switch-inner-unchecked) {
                outline: 0px;
                background: rgba(255, 255, 255, 0.45) !important;
              }
              div:global(.ant-switch-inner-checked) {
                background-color: #5f9d39 !important;
                outline: 0px;
              }
            `}</style>
            <div className="mb-7 flex items-center justify-between ">
              <p className="text-xl font-semibold">Mint Info</p>
              <div className="flex items-center justify-end gap-2">
                <div className="text-base font-medium text-neutral-100">
                  Free mint
                </div>
                <Switch
                  onChange={(checked) => {
                    setOpenFreeMint(checked);
                    if (checked) {
                      form.setFieldValue('whitelist_price', 0);
                    }
                  }}
                  checked={openFreeMint}
                />
              </div>
            </div>
            <div className="grid-col-1 mt-8 grid gap-4 lg:grid-cols-2">
              <div className="relative">
                <p className="absolute right-0 top-0 text-neutral-400">
                  {guess} Timezone
                </p>
                <Form.Item
                  className="w-full"
                  label="Mint Date"
                  name="mint_date"
                  initialValue={null}
                >
                  <DatePicker
                    className="w-full"
                    size="large"
                    allowClear={!mintTime}
                  />
                </Form.Item>
              </div>
              <div className="relative">
                <p className="absolute right-0 top-0 text-neutral-400">
                  {guess} Timezone
                </p>
                <Form.Item
                  className="w-full"
                  label="Mint Time"
                  name="mint_time"
                  initialValue={null}
                >
                  <DatePicker.TimePicker className="w-full" size="large" />
                </Form.Item>
              </div>
            </div>
            <div className="flex flex-col gap-3 lg:flex-row">
              <Form.Item
                className="mb-0 w-full"
                label="Supply Count"
                name="supply_count"
                rules={[
                  { pattern: /^(?!-)\d+$/, message: 'must be non-negative' },
                ]}
              >
                <InputNumber
                  className="w-full"
                  size="large"
                  placeholder="10,000"
                  max={2147483647}
                />
              </Form.Item>
              <Form.Item
                className="mb-0 w-full"
                label="Whitelist Count"
                name="whitelist_count"
                rules={[
                  { pattern: /^(?!-)\d+$/, message: 'must be non-negative' },
                ]}
              >
                <InputNumber
                  className="w-full"
                  size="large"
                  placeholder="2,000"
                  max={2147483647}
                />
              </Form.Item>
            </div>
            <div className="mt-6 flex flex-col gap-3 lg:flex-row">
              <Form.Item
                className="mb-0 w-full"
                label="Public Price"
                name="public_price"
                rules={[
                  {
                    pattern: /^\d+(\.\d+)?$/,
                    message: 'must be non-negative',
                  },
                ]}
              >
                <InputNumber
                  className="w-full"
                  size="large"
                  placeholder="0.5"
                  prefix={chainIcon()}
                />
              </Form.Item>
              <Form.Item
                className="mb-0 w-full"
                label="Whitelist Price"
                name="whitelist_price"
                rules={[
                  {
                    pattern: /^\d+(\.\d+)?$/,
                    message: 'must be non-negative',
                  },
                ]}
              >
                <InputNumber
                  className="w-full"
                  size="large"
                  placeholder="0.2"
                  prefix={chainIcon()}
                  disabled={openFreeMint}
                />
              </Form.Item>
            </div>
          </div>
          <div></div>
        </Form>
        {data ? (
          <>
            <p className="mt-7 text-base">
              In order to ensure that the changes you have made are immediately
              applied, you will need to click on &quot;Save project&quot; and
              authorize with the project&apos;s official Twitter account.
            </p>
            <div className="mt-7 flex gap-5 ">
              <div
                onClick={handlerUpdataBefore}
                className="h-[60px] w-full cursor-pointer rounded-full border border-divider-dark bg-transparent text-center text-lg font-medium leading-[60px]"
              >
                Save project
              </div>
              <div
                onClick={handlerSubmit}
                className="h-[60px] w-full cursor-pointer rounded-full bg-primary-500 text-center text-lg font-medium leading-[60px]"
              >
                Submit report
              </div>
            </div>
          </>
        ) : (
          <div
            onClick={handlerAdd}
            className="mt-7 h-[60px] cursor-pointer rounded-full bg-primary-500 text-center text-lg font-medium leading-[60px]"
          >
            Add Project
          </div>
        )}
      </Modal>
    </ConfigProvider>
  );
};

export default AddPrpject;
